import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { GetTeamDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import contractVersions, {
  ContractVersions,
} from 'modules/contracts/store/collections/contractVersions'
import jobLevelsCollection from 'modules/job_catalog/store/collections/levels'
import jobRolesCollection from 'modules/job_catalog/store/collections/roles'
import {
  ResponsivePage,
  type ResponsivePageActionsInterface,
} from 'modules/layout/components/ResponsivePage'
import TeamDetail from 'modules/teams/components/Detail'
import EditModal from 'modules/teams/components/EditModal'
import TeamsMembersModal from 'modules/teams/components/MembersModal'
import RemoveTeamModal from 'modules/teams/components/RemoveTeamModal'
import type { ModalsState } from 'modules/teams/hooks/useModals'
import useModals from 'modules/teams/hooks/useModals'
import type { ITeam } from 'modules/teams/store/models/Team'

interface ContentProps {
  team: ITeam | null | undefined
  canManageTeams: boolean
  modals: ModalsState
  contractVersions: ContractVersions
}

const Content = ({ team, canManageTeams, modals, contractVersions }: ContentProps) => {
  if (!team) return null

  return (
    <>
      {modals.isAddMemberOpen && (
        <TeamsMembersModal
          team={team}
          onClose={modals.onCloseAddMember}
          contractVersions={contractVersions}
        />
      )}

      {modals.isEditOpen && <EditModal team={team} onClose={modals.onCloseEdit} />}

      {modals.isRemoveOpen && <RemoveTeamModal team={team} onClose={modals.onCloseRemove} />}

      <TeamDetail readonly={!canManageTeams} team={team} contractVersions={contractVersions} />
    </>
  )
}

const TeamDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const { data, loading: isLoadingTeam } = useQuery(GetTeamDocument, {
    variables: {
      teamId: Number(id),
    },
    fetchPolicy: 'cache-first',
  })
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const modals = useModals()
  const team = data?.teams.team
  const canManageTeams = data?.teams._policies.teamManage.can ?? false
  const { loading: loadingJobCatalogData } = useAsyncData(
    () =>
      Promise.all([
        jobRolesCollection.fetch(),
        jobLevelsCollection.fetch(),
        contractVersions.fetch({ withCachedFetchParams: true }),
      ]),
    []
  )

  const actions: ResponsivePageActionsInterface = {
    primary: [
      {
        icon: 'add',
        label: i18n.t('teams.members_modal.title'),
        onClick: modals.onOpenAddMember,
        variant: 'primary',
      },
    ],
    secondary: [
      { onClick: modals.onOpenEdit, title: i18n.t('teams.edit') },
      {
        onClick: modals.onOpenRemove,
        title: i18n.t('teams.delete'),
        danger: true,
      },
    ],
  }

  // todo: passs down title and description error to responsive page
  // title: i18n.t('errors.page_not_found.title'),
  // description: i18n.t('errors.page_not_found.description')

  return (
    <ResponsivePage.Body
      showHeader={false}
      actions={canManageTeams ? actions : undefined}
      loading={isLoadingTeam || loadingJobCatalogData}
      error={!team}
      onPrimaryActionClick={() => {
        team && navigate(navigation.employees.teams.id.addMembers.fullPath({ id: team.id }))
      }}
      onSecondaryActionClick={(key) => {
        if (key === 'edit-team') {
          team && navigate(navigation.employees.teams.id.edit.fullPath({ id: team.id }))
        } else if (key === 'delete-team') {
          team && navigate(navigation.employees.teams.id.delete.fullPath({ id: team.id }))
        }
      }}
    >
      <Content
        canManageTeams={canManageTeams}
        team={
          team && {
            id: team.id,
            name: team.name,
            description: team.description ?? '',
            avatar: team.avatar ?? '',
          }
        }
        modals={modals}
        contractVersions={contractVersions}
      />
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default observer(TeamDetailPage)
